<template>
    <dashboard-layout title="근로계약 출근내역" fullHeight>
        <template slot="topBtn">
            <v-row class="ma-n4">
                <v-col cols="auto" class="pa-4">
                    <attendance-log-init v-on="{ search }" @input="updateItem">
                        <template #activator="{ attrs, on }">
                            <v-btn color="primary" v-bind="attrs" v-on="on" class="min-w-100px min-w-lg-140px">
                                <v-icon class="mr-8">mdi-login</v-icon>
                                <span> 출근하기 </span>
                            </v-btn>
                        </template>
                    </attendance-log-init>
                </v-col>
                <v-col cols="auto" class="pa-4">
                    <attendance-log-done v-on="{ search }" @input="updateItem">
                        <template #activator="{ attrs, on }">
                            <v-btn color="secondary" v-bind="attrs" v-on="on" class="min-w-100px min-w-lg-140px">
                                <v-icon class="mr-8">mdi-logout</v-icon>
                                <span> 퇴근하기 </span>
                            </v-btn>
                        </template>
                    </attendance-log-done>
                </v-col>
            </v-row>
        </template>
        <template slot="contents">
            <attendance-log-list-filter class="mb-12 mb-lg-20" style="flex: 0 0 auto" />

            <template v-if="logs.length">
                <v-data-table
                    v-bind="{
                        items,
                        headers,
                        mobileBreakPoint: $vuetify.breakpoint.thresholds.sm,
                    }"
                    disable-filtering
                    disable-pagination
                    disable-sort
                    hide-default-footer
                    class="v-sheet--outlined rounded-0"
                >
                    <template v-for="header in headers.filter((header) => header.hasOwnProperty('formatter'))" #[`item.${header.value}`]="{ value, item }">
                        <div :key="header.value">{{ header.formatter(value, item) }}</div>
                    </template>
                    <template #[`item.subject`]="{ value, item }">
                        <v-row class="ma-n4" align="center">
                            <v-col class="pa-4" cols="auto">
                                {{ value }}
                            </v-col>
                            <v-col class="pa-4" cols="auto">
                                <employment-contract-view :value="{ _id: item._contract }">
                                    <template #activator="{ attrs, on }">
                                        <v-btn v-bind="attrs" v-on="on" small text icon>
                                            <v-icon> mdi-file-document </v-icon>
                                        </v-btn>
                                    </template>
                                </employment-contract-view>
                            </v-col>
                        </v-row>
                    </template>
                </v-data-table>
                <v-pagination :value="page" :length="pageCount" :total-visible="11" color="primary" class="my-16" @input="(page) => $router.push({ query: { ...$route.query, page } })"></v-pagination>
            </template>

            <template v-else>
                <v-layout column justify-center align-center class="w-100 h-100 grey-f7 rounded-lg pa-20">
                    <p class="page-text--lg line-height-15 text-center mb-12 mb-lg-32">
                        안녕하세요, 회원님. <br />
                        출근내역이 없습니다.
                    </p>
                </v-layout>
            </template>

            <v-fade-transition>
                <v-overlay v-show="loading" color="white">
                    <v-progress-circular size="100" width="5" color="primary" indeterminate />
                </v-overlay>
            </v-fade-transition>
        </template>
    </dashboard-layout>
</template>

<script>
import api from "@/api";
import { attrs_switch } from "@/assets/variables";
import { ATTENDANCE_LOG_TYPES } from "@/store/attendance";

import DashboardLayout from "@/components/dashboard/dashboard-layout.vue";
import AttendanceLogDone from "@/components/dashboard/person/attendance/log/attendance-log-done.vue";
import AttendanceLogInit from "@/components/dashboard/person/attendance/log/attendance-log-init.vue";
import AttendanceLogListFilter from "@/components/dashboard/person/attendance/log/attendance-log-list-filter.vue";
import EmploymentContractView from "@/components/dashboard/common/employment/contract/employment-contract-view.vue";

const headers = [
    {
        text: "#",
        value: "index",
        width: 80,
        align: "end",
    },
    {
        text: "회사명",
        value: "company.name",
        width: 160,
    },
    {
        text: "계약명",
        value: "subject",
    },
    {
        text: "유형",
        value: "type",
        width: 100,
        align: "center",
        formatter: (value) => ATTENDANCE_LOG_TYPES[value]?.text || "-",
    },
    {
        text: "일시",
        value: "date",
        formatter: (value) => value?.toDateTime?.() || value || "-",
        width: 128,
    },
].map((item, index, array) => ({ ...item, divider: index < array.length - 1 }));

export default {
    components: {
        DashboardLayout,
        AttendanceLogDone,
        AttendanceLogInit,
        AttendanceLogListFilter,
        EmploymentContractView,
    },
    data: () => ({
        logs: [],
        summary: { totalCount: 0 },

        limit: 10,
        loading: false,

        headers,
        attrs_switch,
    }),
    computed: {
        items() {
            return this.logs.map((item, index) => ({ ...item, index: this.summary.totalCount - (this.page - 1) * this.limit - index }));
        },
        page() {
            return +(this.$route.query.page || "1");
        },
        skip() {
            return (this.page - 1) * this.limit;
        },
        pageCount() {
            return Math.ceil(this.summary.totalCount / this.limit) || 1;
        },
        params() {
            let { ...query } = this.$route.query;

            delete query.page;

            return { ...query };
        },
    },
    mounted() {
        this.init().then(this.search);
    },
    watch: {
        params() {
            this.init().then(this.search);
        },
        limit() {
            this.init().then(this.search);
        },
    },
    methods: {
        async init() {
            this.logs = [];
            this.summary = { totalCount: 0 };
            this.selected = [];
        },

        async search() {
            if (this.loading) return;
            else this.loading = true;
            try {
                let { skip, limit, params } = this;
                const { summary, logs } = await api.v1.person.attendance.logs.gets({
                    headers: { skip, limit },
                    params,
                });
                this.summary = summary;
                this.logs = logs;
            } finally {
                this.loading = false;
            }
        },

        async remove(item) {
            if (!confirm("삭제하시겠습니까?")) return;

            if (this.loading) return;
            else this.loading = true;

            try {
                await api.v1.person.attendance.logs.delete({ _id: item._id });

                alert("삭제되었습니다.");
            } finally {
                this.loading = false;
                this.search();
            }
        },

        updateItem(item) {
            let index = this.logs.findIndex(({ _id }) => _id == item._id);
            if (-1 < index) this.logs.splice(index, 1, item);
            else this.logs = [item, ...this.logs];
            this.search();
        },
    },
};
</script>

<style lang="scss" scoped>
::v-deep {
    th,
    td {
        &:first-child {
            border-right: 1px solid var(--border-color);

            .v-input--selection-controls__input {
                margin: 0;

                i.v-icon {
                    &.mdi-checkbox-marked,
                    &.mdi-minus-box {
                        color: var(--v-primary-base) !important;
                    }
                }
            }
        }
    }
}
</style>
